import { useId } from './hooks/use-id';
import { useBackgroundColorable } from './mixins/background-color';
import { useSpaceable } from './mixins/spacing';
import { useVisuallyHidable } from './mixins/visually-hidden';
import { HTMLProps, UILibraryComponentProps } from './types';

/**
 * Removes any undefined entries from an object
 */
function filterUndefined(obj: { [key: string]: any }) {
  const filtered: { [key: string]: any } = {};
  for (const [key, val] of Object.entries(obj)) {
    if (val !== undefined) {
      filtered[key] = val;
    }
  }

  return filtered;
}

/**
 * Wraps a chain of use*Props and finalizes the result so that they can be spread on to a React component
 *
 * e.g. const spreadProps = makeSpreadProps(useVisageProps(props)) ... <div {...spreadProps} />
 */
export function makeSpreadProps<P extends { [key: string]: any }>(props: P) {
  return filterUndefined(props);
}

/**
 * Hook that will add the UI Library's shared functionality to a component
 *
 * It accepts all of your component's props.
 * It will process those props (maybe modifying some).
 * And finally, it will return a props object which doesn't contain any of those fancy props
 */
export function useVisageProps<P extends HTMLProps & UILibraryComponentProps>(props: P) {
  // each hook takes in some props and returns those same props (minus any props that the hook "owns")

  // as we add more shared functionality to our comps, we'll add more hooks here
  // the order in which these are composed _shouldn't_ matter - i.e. we should not have 2 hooks which care about the same props
  return (
    // spaceable
    useSpaceable(
      // background-colorable
      useBackgroundColorable(
        // visually-hidable
        useVisuallyHidable(props),
      ),
    )
  );
}

/**
 * Accepts some props that might or might not have an id and returns props that definitely have an id
 *
 * If an id was initially supplied, it will be returned untouched
 * If an id was not initially supplied, an auto-generated id will be provided
 */
export function usePropsWithId<P extends { id?: string }>(props: P) {
  const { id: customId } = props;
  const id = useId(customId);
  return { ...props, id };
}
